import {Button} from '@chekinapp/ui';
import {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSchema} from 'context/guest';
import passportIcon from 'assets/icons/passport-icon-standar.svg?no-inline';
import {Select} from '@guestapp/old-ui-kit';
import {CountriesSelect, useStepperStore} from '@chekin/shared';
import {useDocumentSelection} from 'hooks/iv/useDocumentSelection';
import {
  useVirtualKeyboard,
  useVirtualKeyboardSearchSelects,
} from 'hooks/useVirtualKeyboard';
import {FormTypes} from './types';
import {useInitDocumentType, useInitNationality} from './init';
import {
  PassportIcon,
  PassportTextContainer,
  SelectsContainer,
  Text,
  Wrapper,
} from './styled';
import {getDocumentsListAsOptions, GUEST_FORM_NAMES} from '@chekin/core';

function DocumentSelectionView() {
  const {control, watch, handleSubmit, setValue} = useForm<FormTypes>();
  const {t} = useTranslation();
  const {resetIVProcess} = useStepperStore();
  const {confirm: onConfirm, isLoading} = useDocumentSelection();

  const {register, fields} = useVirtualKeyboardSearchSelects({
    fieldIds: ['nationality'],
  });
  const {handleFocus, handleBlur, keyboard} = useVirtualKeyboard({fields});
  const nationalityField = register('nationality');

  const {
    reservationDocumentsList,
    setNationality: setGuestNationality,
    isGuestSchemaFetching,
  } = useSchema();

  const nationality = watch(GUEST_FORM_NAMES.nationality);
  const documentType = watch(GUEST_FORM_NAMES.document_type);
  const documentOptions = getDocumentsListAsOptions(reservationDocumentsList);

  useInitNationality({
    setValue,
  });
  useInitDocumentType({
    setValue,
    options: documentOptions,
    nationality,
  });

  useEffect(() => {
    setGuestNationality?.(nationality?.value);
  }, [nationality, setGuestNationality]);

  useEffect(() => {
    resetIVProcess();
  }, [resetIVProcess]);

  return (
    <Wrapper className="iv-document-setup-view">
      <PassportTextContainer className="iv-document-setup-view__text">
        <PassportIcon src={passportIcon} width={34} height={48} className="w-34 h-12" />
        <Text>{t('the_document_select_will_be_used_for_check_in_form')}</Text>
      </PassportTextContainer>
      <SelectsContainer className="iv-document-setup-view__fields">
        <Controller
          name={GUEST_FORM_NAMES.nationality}
          rules={{required: t('required')}}
          control={control}
          render={({
            field: {onChange, onBlur: controllerOnBlur, ...otherFields},
            fieldState: {error},
          }) => {
            return (
              <CountriesSelect
                label={t('country_of_nationality')}
                placeholder={t('country_of_nationality')}
                error={error?.message}
                inputValue={nationalityField.value}
                onInputChange={nationalityField.onInputChange}
                onFocus={() => handleFocus('nationality')}
                onBlur={() => {
                  controllerOnBlur();
                  handleBlur('nationality');
                }}
                onChange={nationalityField.onChange?.(value => {
                  onChange(value);
                  setValue(GUEST_FORM_NAMES.document_type, null);
                })}
                {...otherFields}
              />
            );
          }}
        />
        <br />
        {nationality && (
          <Controller
            name={GUEST_FORM_NAMES.document_type}
            rules={{required: t('required')}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={t('select_document')}
                options={documentOptions}
                placeholder={t('select_document')}
                error={error?.message}
                loading={isGuestSchemaFetching}
                {...field}
              />
            )}
          />
        )}
      </SelectsContainer>
      {nationality && documentType && (
        <Button
          onClick={handleSubmit(onConfirm)}
          disabled={isLoading}
          className="mx-auto block"
        >
          {t('confirm')}
        </Button>
      )}
      {keyboard}
    </Wrapper>
  );
}

export {DocumentSelectionView};
